import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { developmentApi, authApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
export interface ChangeAssigneeModalProps {
    isOpen: boolean;
    development: {
        id: number;
        code: string;
        title: string;
        assigned_id?: number | null;
    } | null;
    onClose: () => void;
    onSuccess: (updated: {
        assigned_id: number | null;
        assigned: { id: number; name: string } | null;
    }) => void;
}

interface SystemUser {
    id: number;
    name: string;
    email: string;
    roles: string[];
}

export const ChangeAssigneeModal: React.FC<ChangeAssigneeModalProps> = ({
    isOpen,
    development,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess } = useToast();
    const [selectedUserId, setSelectedUserId] = useState<string | number>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<SystemUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && development) {
            setSelectedUserId(development.assigned_id || "");
            setError(null);
        }
    }, [isOpen, development]);

    useEffect(() => {
        if (isOpen && users.length === 0) {
            setLoadingUsers(true);
            authApi
                .users()
                .then((res) => {
                    if (res.result) {
                        setUsers(res.data);
                    }
                })
                .catch(() => undefined)
                .finally(() => setLoadingUsers(false));
        }
    }, [isOpen, users.length]);

    const userOptions = useMemo(() => {
        const list = users
            .filter(
                (u) =>
                    u.roles &&
                    (u.roles.includes("root") ||
                        u.roles.includes("admin") ||
                        u.roles.includes("it") ||
                        u.roles.includes("development"))
            )
            .map((u) => {
                return {
                    value: String(u.id),
                    label: u.name,
                    subtitle: u.email,
                };
            });

        list.unshift({
            value: "",
            label: t("developments.detail.unassigned"),
            description: "",
        });

        return list;
    }, [users, t]);

    const handleSubmit = async (): Promise<void> => {
        if (!development) {
            return;
        }

        setLoading(true);
        setError(null);

        const assignedItId = selectedUserId ? Number(selectedUserId) : null;

        try {
            const res = await developmentApi.update(development.id, {
                assigned_id: assignedItId,
            });

            if (res.result) {
                showSuccess(t("developments.modal_priority.success"));
                const assignedUser = assignedItId
                    ? users.find((u) => u.id === assignedItId)
                    : null;
                onSuccess({
                    assigned_id: assignedItId,
                    assigned: assignedUser
                        ? { id: assignedUser.id, name: assignedUser.name }
                        : null,
                });
                onClose();
            } else {
                setError(res.message || t("developments.modal_priority.error"));
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_priority.error")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("developments.detail.change_assignee")}
            maxWidth="sm"
            onSubmit={handleSubmit}
            submitLabel={t("modal.save")}
            submittingLabel={t("developments.modal_priority.saving")}
            isSubmitting={loading || loadingUsers}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 mb-3">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {development && (
                <div className="text-center mb-4">
                    <Badge value={development.code} />
                    <div className="mt-2 text-sm text-slate-800 dark:text-zinc-200">
                        {development.title}
                    </div>
                </div>
            )}

            <Select
                value={selectedUserId}
                onChange={(val) => setSelectedUserId(val)}
                options={userOptions}
                disabled={loadingUsers}
                full={true}
                searchable={true}
            />
        </Modal>
    );
};
